import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApprovalFlowModal } from '@/accessControl/ApprovalFlowModal.molecule';
import { ApprovalStepModal } from '@/accessControl/ApprovalStepModal';
import { useApprovalFlows } from '@/accessControl/hooks/useApprovalFlows.hooks';
import { useLockAndVersion } from '@/accessControl/useLockAndVersion.hook';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { LoadingSpinner } from '@/main/LoadingSpinner.molecule';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { isApprovalFlowsEnabled } from '@/services/systemConfiguration.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import { useGetWorkbook } from '@/workbook/hooks/useGetWorkbook.hook';
import { normalizeGqlApprovalFlowData } from '@/worksheet/worksheet.utilities';
import { useApiCreateApprovalFlowAssignmentMutation } from '@seeqdev/api/endpoint/ApprovalFlowAssignmentsApi';
import { useApiCreateApprovalFlowAttachmentMutation } from '@seeqdev/api/endpoint/ApprovalFlowAttachmentsApi';
import { GetVersionHistoriesQueryKey, useApiGetVersionHistoriesQuery } from '@seeqdev/api/endpoint/AuditApi';
import { Badge, Button, ButtonWithDropdown, Icon } from '@seeqdev/qomponents';
import type { VersionState } from '@/core/hooks/useVersionState.hook';
import type { ApprovalFlowAssignmentOutputV1 } from '@seeqdev/api/model/models';
import type { VersionHistoryOutputV1 } from '@seeqdev/api/model/models';

type ActiveModal = null | 'FLOW_MODAL' | 'STEP_MODAL';

export const ApprovalFlowHeaderControl = () => {
  const { t } = useTranslation();
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const { approvalFlow, nextStep, futureStep, showApplyBtn, showStepDropdown, workbookId, approved } =
    useApprovalFlows();
  const { isLocked } = useFluxPaths(sqWorkbookStore, ['isLocked']);
  const { stateParams } = useFluxPaths(sqWorkbenchStore, ['stateParams']);
  const { worksheetId, workstepId } = stateParams;
  const approvalFlowsEnabled = isApprovalFlowsEnabled();
  const isTopic = useWorkbookIsTopic();
  const queryClient = useQueryClient();
  const itemType = isTopic ? t('APPROVAL_FLOWS.ITEM_TYPES.TOPIC') : t('APPROVAL_FLOWS.ITEM_TYPES.ANALYSIS');
  const { isLoading, data } = useApiGetVersionHistoriesQuery(
    { versionedItemType: [isTopic ? 'Organizer' : 'Analysis'], itemId: workbookId },
    { enabled: Boolean(approvalFlowsEnabled && approvalFlow) },
  );
  const isInProd = data?.versionHistories?.some((versionHistory) =>
    Boolean(
      versionHistory?.versions?.find(
        (version: VersionHistoryOutputV1['versions'][number]) =>
          version?.versionedItem?.id === workbookId && version?.version === versionHistory.productionVersionNumber,
      ),
    ),
  );
  const { fetch } = useGetWorkbook({
    workbookId,
    worksheetId,
    workstepId,
    skipWorksheets: headlessRenderMode(),
  });

  const updateWorksheetStore = async () => {
    try {
      const { data } = await fetch();
      const flowData = normalizeGqlApprovalFlowData(data?.workbook);
      flux.dispatch(
        'WORKBOOK_SET_APPROVAL_FLOW',
        {
          approvalFlows: flowData.approvalFlows,
          approvalFlowAssignments: flowData.approvalFlowAssignments,
          attachments: flowData.attachments,
        },
        PUSH_IGNORE,
      );
    } catch (error) {
      errorToast({ httpResponseOrError: error });
    }
  };
  const { mutate: createApprovalFlowAttachment, isPending: isAttaching } = useApiCreateApprovalFlowAttachmentMutation({
    onSuccess: async () => {
      successToast({ messageKey: 'APPROVAL_FLOWS.APPLY_SUCCESS' });
      setActiveModal(null);
      await updateWorksheetStore();
    },
    onError: (error) => {
      errorToast({ httpResponseOrError: error });
    },
  });

  const {
    mutate: createAssignment,
    mutateAsync: createAssignmentPromise,
    isPending: isAssigning,
  } = useApiCreateApprovalFlowAssignmentMutation({
    onSuccess: async () => {
      successToast({ messageKey: 'APPROVAL_FLOWS.PROMOTE_SUCCESS' });
      setActiveModal(null);
      await updateWorksheetStore();
      await queryClient.invalidateQueries({
        queryKey: GetVersionHistoriesQueryKey({
          versionedItemType: [isTopic ? 'Organizer' : 'Analysis'],
          itemId: workbookId,
        }),
      });
    },
    onError: (error) => {
      errorToast({ httpResponseOrError: error });
    },
  });

  const { lockAndVersion, hasLockingFailedPreviouslyDueToGlobalItems } =
    useLockAndVersion<ApprovalFlowAssignmentOutputV1>({
      itemId: workbookId,
      onBeforeRedirect: () => {
        if (!approvalFlow || !nextStep) {
          return Promise.resolve({} as ApprovalFlowAssignmentOutputV1);
        }
        return createAssignmentPromise([{ stepId: nextStep?.id, itemId: workbookId }, { flowId: approvalFlow?.id }]);
      },
      closeModal: () => setActiveModal(null),
    });

  const applyApprovalFlow = async () => {
    approvalFlow && createApprovalFlowAttachment([{ itemId: workbookId }, { flowId: approvalFlow?.id }]);
  };

  const approveStep = async (step: NonNullable<typeof nextStep>, versionState?: VersionState) => {
    const needsLocking = step.lockRequired && !isLocked;
    if (needsLocking && versionState) {
      try {
        return await lockAndVersion(versionState);
      } catch (error) {
        return errorToast({ httpResponseOrError: error });
      }
    }
    return approvalFlow && createAssignment([{ stepId: step.id, itemId: workbookId }, { flowId: approvalFlow?.id }]);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!approvalFlowsEnabled || (!showApplyBtn && !showStepDropdown)) {
    return null;
  }
  if (isInProd) {
    return <Badge variant="neutral">{t('APPROVAL_FLOWS.IN_PROD')}</Badge>;
  }
  if (approved) {
    return <Badge variant="neutral">{t('APPROVAL_FLOWS.DEPRECATED')}</Badge>;
  }
  return (
    <>
      {showApplyBtn ? (
        <Button
          size="xs"
          label={t('APPROVAL_FLOWS.APPLY_TITLE')}
          onClick={() => setActiveModal('FLOW_MODAL')}
          testId="applyApprovalFlow"
          variant="outline"
        />
      ) : showStepDropdown ? (
        <ButtonWithDropdown
          label={
            <>
              <span>{nextStep?.name}</span>
              <Icon
                icon="fa-chevron-down"
                testId="approvalFlowStepChevron"
                type="inherit"
                extraClassNames="tw:pl-0.5"
              />
            </>
          }
          size="xs"
          data-testid="approvalFlowStep"
          dropdownItems={[
            {
              label: t('APPROVAL_FLOWS.OPTIONS.APPROVE_STEP'),
              disabled: !nextStep?.permissions.manage,
              onClick: () => setActiveModal('STEP_MODAL'),
            },
            {
              label: t('APPROVAL_FLOWS.OPTIONS.VIEW_STEPS'),
              onClick: () => setActiveModal('FLOW_MODAL'),
            },
          ]}
        />
      ) : null}
      {activeModal === 'FLOW_MODAL' && (
        <ApprovalFlowModal
          isApplyingFlow={showApplyBtn}
          approvalFlow={approvalFlow}
          itemType={itemType}
          isApplying={isAttaching}
          onSubmit={showApplyBtn ? applyApprovalFlow : async () => setActiveModal(null)}
          onClose={() => setActiveModal(null)}
        />
      )}
      {activeModal === 'STEP_MODAL' && nextStep && (
        <ApprovalStepModal
          step={nextStep}
          futureStep={futureStep}
          approvalFlow={approvalFlow}
          itemType={itemType}
          isSubmitting={isAssigning}
          hasLockingFailedPreviouslyDueToGlobalItems={hasLockingFailedPreviouslyDueToGlobalItems}
          onSubmit={async (versionState) => {
            await approveStep(nextStep, versionState);
          }}
          onClose={() => setActiveModal(null)}
          isItemLocked={isLocked}
        />
      )}
    </>
  );
};
